/** Small pure helpers for the field day and the to-invoice card (F1, F5). */

/** Finished work: it leaves the tech's "Today" list and the "next job" choice. */
export function isFinished(status: string): boolean {
  // Cancelled too: it is not something to do today either.
  return status === "completed" || status === "invoiced" || status === "cancelled";
}

/** Today's jobs split into what is still to do and what is done. Order is kept. */
export function splitDone<T extends { status: string }>(jobs: T[]): { todo: T[]; done: T[] } {
  return { todo: jobs.filter((j) => !isFinished(j.status)), done: jobs.filter((j) => isFinished(j.status)) };
}

/** Past this many days a finished job nobody has invoiced is flagged. */
export const WAITING_WARN_DAYS = 7;

export function waitingLabel(days: number | null | undefined): string {
  if (days === null || days === undefined) return "—";
  return days === 1 ? "1 day" : `${days} days`;
}

export function waitingTooLong(days: number | null | undefined): boolean {
  return days !== null && days !== undefined && days > WAITING_WARN_DAYS;
}

/**
 * Whole calendar days since a moment, counted in the business's own day
 * (Adelaide), not UTC: a job left at 4pm yesterday is "1 day", however the
 * clock reads in Greenwich. Null when there is no timestamp.
 */
export function daysSince(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const asDay = (d: Date) => {
    const [y = 0, m = 1, day = 1] = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Australia/Adelaide",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .format(d)
      .split("-")
      .map(Number);
    return Date.UTC(y, m - 1, day);
  };
  return Math.max(0, Math.round((asDay(now) - asDay(then)) / 86_400_000));
}

/** Why a tech left a job unfinished (D87), in the words the screens use. */
export const RETURN_REASON_LABEL: Record<string, string> = {
  parts: "Parts needed",
  more_time: "More time needed",
  customer: "Customer not available / asked us to stop",
  other: "Other",
};

export function returnReasonLabel(reason: string | null | undefined): string {
  return (reason && RETURN_REASON_LABEL[reason]) || "Coming back";
}
